import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, Lock, Key, Database, Facebook } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/ui/logo";
import { supabase } from "@/integrations/supabase/client";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [oauthLoading, setOAuthLoading] = useState(false);
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const navigate = useNavigate();
  const { login, isAuthenticated, isAdmin, isLoading } = useAuth();

  useEffect(() => {
    console.log("AdminLogin useEffect - Auth Status:", { 
      isAuthenticated, 
      isAdmin, 
      isLoading 
    });
    
    if (!isLoading && isAuthenticated && isAdmin) {
      console.log("Admin already authenticated, redirecting to admin dashboard");
      navigate("/admin/dashboard", { replace: true });
    }
    else if (!isLoading && isAuthenticated && !isAdmin) {
      console.log("User authenticated but not admin, redirecting to home");
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate, isLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLocalLoading(true);
    
    try {
      if (!email || !password || !accessCode) {
        toast.error("Bitte füllen Sie alle Felder aus.");
        return;
      }
      
      // Test Account Logik
      if (email === "admin@test.de" && password === "Test123!" && accessCode === "admin123") {
        toast.success("Admin-Anmeldung erfolgreich");
        
        const userData = {
          name: "System Administrator",
          email: email,
          id: "admin-test-id"
        };
        
        login(userData, true);
        navigate("/admin/dashboard", { replace: true });
        return;
      }
      
      // Reguläre Anmeldung mit Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error("Login error:", error);
        toast.error("Die eingegebenen Anmeldedaten sind ungültig.");
        return;
      }
      
      if (data.user) {
        // Prüfe Admin-Rechte
        const { data: isAdminData, error: isAdminError } = await supabase.rpc('is_admin_user');
        
        if (isAdminError || isAdminData === false) {
          await supabase.auth.signOut();
          toast.error("Zugang verweigert. Sie haben keine Administrator-Rechte.");
          return;
        }
        
        toast.success("Admin-Anmeldung erfolgreich");
        navigate("/admin/dashboard", { replace: true });
      }
    } catch (err) {
      console.error("Unexpected login error:", err);
      toast.error("Bei der Anmeldung ist ein Fehler aufgetreten.");
    } finally {
      setIsLocalLoading(false);
    }
  };

  const useTestAccount = () => {
    setEmail("admin@test.de");
    setPassword("Test123!");
    setAccessCode("admin123");
  };

  const handleSetupMetaApp = () => {
    setOAuthLoading(true);
    
    toast("Meta Business Manager", {
      description: "Öffne Meta Business Manager zur Konfiguration...",
    });
    
    setTimeout(() => {
      setOAuthLoading(false);
      window.open('https://business.facebook.com/settings', '_blank');
      
      toast("Meta Business Manager geöffnet", {
        description: "Bitte konfigurieren Sie die App-Einstellungen im Meta Business Manager.",
      });
    }, 1500);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A] text-white">
        <div className="animate-spin h-6 w-6 border-2 border-blue-500 rounded-full border-t-transparent mr-2"></div>
        <span>Authentifizierung wird geprüft...</span>
      </div>
    );
  }

  if (isAuthenticated && isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center overflow-hidden bg-radial-gradient">
      <div className="container max-w-6xl mx-auto px-4 relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
        
        {/* Left content area */}
        <div className="p-8 text-left">
          <div className="mb-8">
            <Logo size="lg" isWhite={true} className="mb-8" />
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4">Willkommen zurück</h1>
          <p className="text-gray-300 mb-8 text-lg">Melden Sie sich an, um auf die Admin-Funktionen zuzugreifen</p>
          
          <div className="space-y-6">
            <div className="flex items-start space-x-4 glass-effect p-5 rounded-xl">
              <div className="bg-[#2D5665] p-3 rounded-full">
                <Shield className="text-[#41c7f0] h-6 w-6" />
              </div>
              <div>
                <h3 className="font-medium text-white">Admin-Zugang</h3>
                <p className="text-gray-400 text-sm">Vollständiger Zugriff auf alle Systemfunktionen und Kundendaten</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4 glass-effect p-5 rounded-xl">
              <div className="bg-[#2D5665] p-3 rounded-full">
                <Database className="text-[#41c7f0] h-6 w-6" />
              </div>
              <div>
                <h3 className="font-medium text-white">Zentrale Meta-App</h3>
                <p className="text-gray-400 text-sm">Meta-Daten für alle Kunden über eine zentrale Business App verwalten</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 glass-effect p-5 rounded-xl">
              <div className="bg-[#2D5665] p-3 rounded-full">
                <Lock className="text-[#41c7f0] h-6 w-6" />
              </div>
              <div>
                <h3 className="font-medium text-white">Sicherheit</h3>
                <p className="text-gray-400 text-sm">Zusätzlicher Zugangscode erforderlich für erhöhte Sicherheit</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Right login form */}
        <div className="glass-effect rounded-2xl p-8 shadow-xl">
          <h2 className="text-2xl font-semibold text-white mb-6 text-center">Administrator-Login</h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-gray-300 mb-2 font-medium">E-Mail</label>
              <Input
                id="email"
                type="email"
                placeholder="name@firma.de"
                className="bg-[#1A2433] border-gray-700 text-white focus:border-[#41c7f0]"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-gray-300 mb-2 font-medium">Passwort</label>
              <Input
                id="password"
                type="password"
                className="bg-[#1A2433] border-gray-700 text-white focus:border-[#41c7f0]"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            
            <div>
              <label htmlFor="accessCode" className="block text-gray-300 mb-2 font-medium">Admin-Zugangscode</label>
              <Input
                id="accessCode"
                type="password"
                className="bg-[#1A2433] border-gray-700 text-white focus:border-[#41c7f0]"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
              />
            </div>
            
            <div className="text-right">
              <a href="#" className="text-[#41c7f0] text-sm hover:underline">Passwort vergessen?</a>
            </div>
            
            <Button 
              type="submit" 
              disabled={isLocalLoading}
              className="w-full bg-[#41c7f0] hover:bg-[#3ab7df] text-white rounded-md disabled:opacity-50"
            >
              {isLocalLoading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent mr-2"></div>
                  Anmeldung läuft...
                </>
              ) : (
                <>
                  <Key className="mr-2 h-4 w-4" />
                  Anmelden
                </>
              )}
            </Button>

            <div className="text-center pt-4 mt-4 border-t border-gray-700">
              <p className="mb-2 text-gray-300">Testaccount verwenden</p>
              <Button 
                type="button"
                variant="outline" 
                onClick={useTestAccount}
                className="w-full border-gray-700 text-gray-300 hover:bg-[#1A2433] rounded-md"
              >
                Test Account laden
              </Button>
            </div>
            
            <div className="text-center pt-4 mt-4 border-t border-gray-700">
              <p className="mb-3 text-gray-400 text-sm">SYSTEM ADMINISTRATOR FUNKTIONEN</p>
              <div className="space-y-3">
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={handleSetupMetaApp}
                  disabled={oauthLoading}
                  className="w-full border-gray-700 text-gray-300 hover:bg-[#1A2433] rounded-md"
                >
                  <Facebook className="mr-2 h-4 w-4 text-[#1877F2]" />
                  Meta Business App konfigurieren
                </Button>
              </div>
            </div>
            
            <div className="text-center text-gray-400 text-sm mt-6">
              <p>Zurück zum <a href="/login" className="text-[#41c7f0] font-medium hover:underline">Kunden-Login</a></p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
